/**
 * PNG export: the SVG, rasterised.
 *
 * A PNG is the one export that genuinely is pixels, and it is still not a screenshot. It is the
 * vector file drawn at a chosen density — so it contains the whole map rather than the visible
 * part, it is sharp at the scale it was asked for, and it comes from the same scene as everything
 * else. What it loses relative to the SVG is exactly what a raster loses, and the user chose that
 * by picking this format.
 *
 * This is the only file in the package that needs a DOM, which is why it is behind its own entry
 * point. The pure half runs in a Cloudflare Worker; when server-side PNGs are wanted, a Worker
 * gets a different rasteriser and none of the rest moves.
 */
import { appError, err, ok } from '@orbit-mind/shared-types';
import { DEFAULT_RASTER_SCALE, PNG_MIME_TYPE, rasterSizeFor, } from '../raster-size';
import { exportViewBox, sceneToSvg, SVG_MIME_TYPE } from '../svg';
// The sizing decision lives on the package root, shared with the phone's rasteriser. Re-exported
// so everything that imported it from here keeps working.
export { DEFAULT_RASTER_SCALE, MAX_RASTER_EDGE, PNG_MIME_TYPE, rasterSizeFor, } from '../raster-size';
/**
 * Draws an SVG string onto a canvas and reads back a PNG.
 *
 * The SVG goes in through an `<img>`, which renders it in a restricted mode: no external
 * requests, no scripts. That suits an export — the file is already self-contained — and locally
 * installed fonts named in `font-family` still resolve, which is why the text does not fall back
 * to a serif.
 */
export async function svgToPng(svg, options) {
    const size = rasterSizeFor(options.width, options.height, options.scale ?? DEFAULT_RASTER_SCALE);
    const url = URL.createObjectURL(new Blob([svg], { type: SVG_MIME_TYPE }));
    try {
        const image = await loadImage(url);
        if (!image.ok)
            return image;
        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const context = canvas.getContext('2d');
        if (context === null) {
            return err(appError('INTERNAL', 'this browser would not provide a 2D canvas', { retryable: false }));
        }
        if (options.background !== null && options.background !== undefined) {
            context.fillStyle = options.background;
            context.fillRect(0, 0, size.width, size.height);
        }
        context.drawImage(image.value, 0, 0, size.width, size.height);
        const blob = await toBlob(canvas, options.mimeType);
        if (!blob.ok)
            return blob;
        return ok({ blob: blob.value, size });
    }
    finally {
        // Released whatever happened. An object URL holds its blob alive until it is revoked, and an
        // export that failed is exactly the case where nobody remembers to.
        URL.revokeObjectURL(url);
    }
}
/** The scene as a PNG, framed identically to the SVG it was rasterised from. */
export async function sceneToPng(scene, options = {}) {
    const view = exportViewBox(scene.bounds, options.padding);
    const svg = sceneToSvg(scene, options);
    return svgToPng(svg, {
        width: view.width,
        height: view.height,
        ...(options.scale === undefined ? {} : { scale: options.scale }),
        // The SVG already paints its own background, so painting a second one under it would only
        // matter when the caller asked for none — and then they meant none.
        background: null,
        ...(options.mimeType === undefined ? {} : { mimeType: options.mimeType }),
    });
}
function loadImage(url) {
    return new Promise((resolve) => {
        const image = new Image();
        image.addEventListener('load', () => {
            resolve(ok(image));
        });
        image.addEventListener('error', () => {
            // Reached when the SVG is not well-formed. Reported rather than thrown: the caller is a
            // menu item, and a menu item needs something to say.
            resolve(err(appError('INTERNAL', 'the exported drawing could not be rendered', { retryable: false })));
        });
        image.src = url;
    });
}
function toBlob(canvas, mimeType = PNG_MIME_TYPE) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob === null
                ? err(appError('INTERNAL', 'the image was too large for this browser to encode', {
                    details: { width: canvas.width, height: canvas.height },
                    retryable: false,
                }))
                : ok(blob));
        }, mimeType, 0.94);
    });
}
//# sourceMappingURL=raster.js.map